import { inject, Injectable } from "@angular/core";
import { Observable, delay, filter, from, map, of, tap } from "rxjs";
import { SocketTransportService } from "../../../core/realtime/socket-transport.service";
import { RealtimeRoomFacade } from "../../../core/realtime/realtime-room.facade";
import { CallInvitation, CallQuality, CallSession, RemoteStreamType } from "../models/call.models";
import { WebRtcPeerService } from "./web-rtc-peer.service";
import { MediaDevicePreference } from "./media-device-preference.store";
import { UserPresenceFacade } from "../../../core/realtime/user-presence.facade";

/** Socket.IO, RTCPeerConnection and MediaStream instances remain in this layer. */
@Injectable({ providedIn: "root" })
export class CallSignalingGateway {
    private readonly socket = inject(SocketTransportService);
    private readonly rooms = inject(RealtimeRoomFacade);
    private readonly peer = inject(WebRtcPeerService);
    private readonly presence = inject(UserPresenceFacade);

    readonly askForVideo$ = this.socket.on<unknown>("ask_for_video");
    readonly videoAccepted$ = this.socket.on<unknown>("video_accepted");
    readonly videoDeclined$ = this.socket.on<unknown>("video_declined");
    readonly qualityRequested$ = this.socket.on<{ quality?: string; value?: string; status?: string }>("video_quality").pipe(
        map(({ quality, value, status }) => this.fromWireQuality(quality ?? value ?? status)),
        filter((quality): quality is CallQuality => quality !== null),
    );
    readonly screenShareEnded$ = this.peer.screenShareEnded$;
    readonly remoteRecordingChanged$: Observable<boolean> = new Observable((subscriber) => {
        const subscriptions = [
            this.socket.on<unknown>("record_stream").subscribe(() => subscriber.next(true)),
            this.socket.on<unknown>("stop_record_stream").subscribe(() => subscriber.next(false)),
        ];
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
    });
    readonly remoteStreamChanged$: Observable<RemoteStreamType> = new Observable((subscriber) => {
        const subscriptions = [
            this.socket.on<unknown>("desktop_stream").subscribe(() => { this.socket.emit("desktop_stream_done", {}); subscriber.next("desktop"); }),
            this.socket.on<unknown>("desktopstream").subscribe(() => { this.socket.emit("desktop_stream_done", {}); subscriber.next("desktop"); }),
            this.socket.on<unknown>("camera_stream").subscribe(() => { this.socket.emit("camera_stream_done", {}); subscriber.next("camera"); }),
            this.socket.on<unknown>("camerastream").subscribe(() => { this.socket.emit("camera_stream_done", {}); subscriber.next("camera"); }),
            this.socket.on<unknown>("canvas_stream").subscribe(() => { this.socket.emit("canvas_stream_done", {}); subscriber.next("drawing"); }),
            this.socket.on<unknown>("canvasstream").subscribe(() => { this.socket.emit("canvas_stream_done", {}); subscriber.next("drawing"); }),
        ];
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
    });

    incoming(invitation: CallInvitation): void {
        if (invitation.source === "demo") return;
        this.presence.forceBusy();
    }

    call(invitation: CallInvitation): Observable<CallSession> {
        this.presence.forceBusy();
        this.rooms.createOrJoin(invitation.callee.username);
        this.socket.emit("init_call", {
            callId: invitation.id,
            audioOnly: invitation.audioOnly,
            caller: invitation.caller,
            callee: invitation.callee,
        });
        return from(this.peer.call(invitation));
    }

    connect(invitation: CallInvitation): Observable<CallSession> {
        if (invitation.source === "demo") {
            return of({
                id: `call-${invitation.id}`,
                participant: invitation.participant,
                startedAt: Date.now(),
                invitation,
            }).pipe(delay(850));
        }
        if (invitation.source === "socket") {
            this.presence.forceBusy();
            this.rooms.createOrJoin(invitation.callee.username);
        }
        return from(this.peer.accept(invitation));
    }

    reject(invitation: CallInvitation): Observable<void> {
        if (invitation.source === "socket") {
            this.socket.emit("cancel", this.endPayload(invitation));
            this.peer.close();
            this.restoreAvailability();
        }
        return of(undefined);
    }

    cancel(invitation: CallInvitation): Observable<void> {
        this.socket.emit("cancel", this.endPayload(invitation));
        this.peer.close();
        this.restoreAvailability();
        return of(undefined);
    }

    end(session: CallSession): Observable<void> {
        if (session.invitation.source === "socket") {
            this.socket.emit("end", this.endPayload(session.invitation));
            this.peer.close();
            this.restoreAvailability();
        }
        return of(undefined).pipe(delay(350));
    }

    remoteEnded(): void { this.peer.close(); this.restoreAvailability(); }
    connectionFailed(): void { this.peer.close(); this.restoreAvailability(); }

    setMuted(muted: boolean): void { this.peer.setMuted(muted); }
    setCameraDisabled(disabled: boolean): void { this.peer.setCameraDisabled(disabled); }
    requestVideo(): void { this.socket.emit("ask_for_video", {}); }
    declineVideo(): void { this.socket.emit("video_declined", {}); }
    enableVideo(): Observable<void> { return from(this.peer.enableCamera()).pipe(map(() => undefined)); }
    acceptVideo(): Observable<void> {
        return this.enableVideo().pipe(
            tap(() => this.socket.emit("video_accepted", {})),
            map(() => undefined),
        );
    }
    startScreenShare(): Observable<void> {
        return from(this.peer.startScreenShare()).pipe(
            tap(() => this.socket.emit("desktop_stream", {})),
            map(() => undefined),
        );
    }
    stopScreenShare(): Observable<void> {
        return from(this.peer.stopScreenShare()).pipe(
            tap(() => this.socket.emit("camera_stream", {})),
            map(() => undefined),
        );
    }
    notifyScreenShareEnded(): void { this.socket.emit("camera_stream", {}); }
    startPointerMode(stream: MediaStream): Observable<void> {
        return from(this.peer.startPointerStream(stream)).pipe(
            tap(() => this.socket.emit("desktop_stream", {})),
            map(() => undefined),
        );
    }
    stopPointerMode(): Observable<void> {
        return from(this.peer.stopPointerStream()).pipe(
            tap(() => this.socket.emit("camera_stream", {})),
            map(() => undefined),
        );
    }
    startDrawingMode(stream: MediaStream): Observable<void> {
        return from(this.peer.startDrawingStream(stream)).pipe(
            tap(() => this.socket.emit("canvas_stream", {})),
            map(() => undefined),
        );
    }
    stopDrawingMode(): Observable<void> {
        return from(this.peer.stopDrawingStream()).pipe(
            tap(() => this.socket.emit("camera_stream", {})),
            map(() => undefined),
        );
    }
    notifySnapshotSaved(): void { this.socket.emit("snapshot"); }
    changeQuality(quality: CallQuality, cameraDisabled: boolean): Observable<void> {
        return from(this.peer.switchQuality(quality, cameraDisabled)).pipe(map(() => undefined));
    }
    acknowledgeQualityChange(): void { this.socket.emit("video_quality_done"); }
    applyMediaDevices(preference: MediaDevicePreference, quality: CallQuality, muted: boolean, cameraDisabled: boolean, videoEnabled: boolean): Observable<void> {
        return from(this.peer.applyDevices(preference, quality, muted, cameraDisabled, videoEnabled)).pipe(map(() => undefined));
    }
    notifyRecordingStarted(): void { this.socket.emit("record_stream"); }
    notifyRecordingStopped(): void { this.socket.emit("stop_record_stream"); }

    private endPayload(invitation: CallInvitation) {
        return { callId: invitation.id, caller: invitation.caller, callee: invitation.callee };
    }

    private fromWireQuality(quality?: string): CallQuality | null {
        if (quality === "hd") return "hd";
        if (quality === "vga" || quality === "sd") return "sd";
        if (quality === "audio_only" || quality === "audio") return "audio";
        return null;
    }

    private restoreAvailability(): void {
        this.rooms.returnToPersonalRoom();
        this.presence.restore();
    }
}
